import { AmountSuperScript } from "components/design/AmountSuperScript";
import { Button } from "components/DesignSystem/Button/Button";
import ChatCard from "components/DesignSystem/ChatCard/v2/ChatCard";
import { AdvanceTaxAndProfitsIcon } from "components/icons/LeftNav/AdvanceTaxAndProfits";
import { DISPLAY_DATE } from "constants/date";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useHistory } from "react-router-dom";
import { renderText } from "stream-chat-react";
import { QuarterStatus } from "types/Models/estimatedTax";

type EstimatedTaxCardMetadata = {
  entity_id: string;
  quarter: number;
  label: string;
  year: number;
  deadline: string;
  status: QuarterStatus;
  estimated_payment: number | null;
  paid_amount: number;
  annual_net_income: number | null;
  dashboard_url: string;
};

type EstimatedTaxCardCustomData = {
  header: string;
  description: string;
  card_status: string;
  metadata: EstimatedTaxCardMetadata;
};

export const EstimatedTaxQuarterlyCard = ({
  customData,
}: {
  customData: EstimatedTaxCardCustomData;
}) => {
  const history = useHistory();
  const { header, description, metadata } = customData || {};
  const { status, deadline, estimated_payment, paid_amount, entity_id, year } =
    metadata || {};

  const renderedDescription = useMemo(
    () => (description ? renderText(description) : null),
    [description]
  );

  const handleSeeDetails = () => {
    history.replace(`/tax/advance-tax?entity=${entity_id}&season=${year}`);
  };

  const showEstimated = status !== "PAID" && paid_amount === 0;
  const showPaid = paid_amount > 0 || estimated_payment === 0;

  return (
    <>
      <ChatCard.Content addMargin={false}>
        <ChatCard.Header icon={<AdvanceTaxAndProfitsIcon />}>
          <div className="t-text-subtext-sm t-text-text-30">{header}</div>
        </ChatCard.Header>
        <ChatCard.Body>
          <div className="t-flex t-flex-col t-gap-2">
            <div className="t-text-body-sm t-text-text-30">
              Due {dayjs(deadline).format(DISPLAY_DATE)}
            </div>
            {showEstimated && (
              <div className="t-mt-1">
                <div className="t-text-body-sm t-text-text-30 t-mb-0.5">
                  Estimated
                </div>
                <AmountSuperScript amount={estimated_payment ?? 0} />
              </div>
            )}
            {showPaid && (
              <div className="t-mt-1">
                <div className="t-text-body-sm t-text-text-30 t-mb-0.5">
                  Paid
                </div>
                <AmountSuperScript amount={paid_amount} />
              </div>
            )}
            <Button
              customType="primary"
              block
              size="small"
              onClick={handleSeeDetails}
            >
              See Details
            </Button>
          </div>
        </ChatCard.Body>
      </ChatCard.Content>
      {renderedDescription && (
        <span className="[&>p:last-child]:t-m-0 [&_p]:t-whitespace-pre-line [&_ul]:t-mb-4 [&_ol]:t-mb-4">
          {renderedDescription}
        </span>
      )}
    </>
  );
};
